import React, { createContext, useContext, useState, useEffect, FunctionComponent, ReactNode } from 'react';
import { MetaLibrarySystemModel } from '../models/meta-library.model';
import { MetaLibraryService } from '../services/meta-library.service';
import { useUserContext } from '../../user/contexts/user.context';

interface MetaLibraryContextType {
  systems: MetaLibrarySystemModel[];
  isLoading: boolean;
  error: Error | null;
  refreshMetaLibrary: () => Promise<void>;
}

const MetaLibraryContext = createContext<MetaLibraryContextType | undefined>(undefined);

interface MetaLibraryProviderProps {
  children: ReactNode;
}

export const MetaLibraryProvider: FunctionComponent<MetaLibraryProviderProps> = ({ children }) => {
  const [systems, setSystems] = useState<MetaLibrarySystemModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [cachedOrgaId, setCachedOrgaId] = useState<string | null>(null);
  const { currentOrganization } = useUserContext();

  const loadMetaLibrary = async (orgaId: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await MetaLibraryService.getMetaLibrary(orgaId);
      setSystems(data);
      setCachedOrgaId(orgaId);
    } catch (err) {
      setError(err as Error);
      console.error('Erreur lors du chargement de la meta library:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshMetaLibrary = async () => {
    const orgaCode = currentOrganization?.code;
    if (orgaCode) {
      await loadMetaLibrary(orgaCode);
    }
  };

  useEffect(() => {
    const orgaCode = currentOrganization?.code;
    if (!orgaCode) return;

    // Charger les données uniquement si :
    // 1. Aucune donnée n'est en cache (cachedOrgaId === null)
    // 2. L'organisation a changé (cachedOrgaId !== currentOrganization.code)
    if (cachedOrgaId !== orgaCode) {
      loadMetaLibrary(orgaCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization?.code, cachedOrgaId]);

  return (
    <MetaLibraryContext.Provider value={{ systems, isLoading, error, refreshMetaLibrary }}>
      {children}
    </MetaLibraryContext.Provider>
  );
};

export const useMetaLibraryContext = (): MetaLibraryContextType => {
  const context = useContext(MetaLibraryContext);
  if (context === undefined) {
    throw new Error('useMetaLibraryContext doit être utilisé à l\'intérieur d\'un MetaLibraryProvider');
  }
  return context;
};

